/**
 * The composed spec synthesis time budget: ONE derivation every hop of the
 * wizard's "Adapt this template to my idea" chain reads. The hops are the
 * Builder's synthesis (each model call, each admission proof), the
 * `synthesizeComposedSpecFn` request to the Builder, that Function's own
 * timeout, the portal's callable wait and the live rehearsal's wait.
 *
 * Import-free on purpose: plain-node tools (tools/qa) import this file
 * directly through Node's type stripping.
 *
 * Synthesis is at most {@link COMPOSED_SPEC_MAX_CALLS} rounds, each one
 * bounded model call plus, when the patch derives, one admission proof. The
 * proof's independent solver runs (both proof seeds and every stub check)
 * already run concurrently on the Builder's worker threads, so a proof costs
 * its slowest single solver run.
 *
 * Proof sizing (2026-10-29). A proof costs its slowest SEED: the proof seeds
 * and stub checks run in parallel, and the seeds start first. Each seed
 * (solver match + idle match) is bounded by its own thread CPU
 * ({@link COMPOSED_PROOF_SEED_MAX_CPU_MS}), sized to admit the slowest
 * catalog seed at the production slowdown; the proof's wall bound is that
 * plus a reserve for stub checks queued behind the seeds. The composed
 * Create certification checks every template at the measured slowdown.
 *
 * Model calls (production claude-sonnet-5, 2026-10-15..19, n=24 successful):
 * p50 15.5 s, p95 25.1 s, max 27.6 s for up to ~2,800 output tokens. A one-minute
 * bound aborted both Scoop Rush synthesis calls at exactly 60.0 s with 0 output
 * tokens (provider latency, 2026-10-19). One call, including chatBounded's single
 * truncation retry, is bounded at three minutes: ~6.5x the slowest observed call.
 */

/** 1 synthesis call + at most 2 data-only repair rounds (owner budget). */
export const COMPOSED_SPEC_MAX_CALLS = 3;
/** Wall bound of one synthesis model call (its truncation retry included). */
export const COMPOSED_SPEC_CALL_TIMEOUT_MS = 3 * 60_000;
/** Production/local ratio of the admission proof: the worst measured on
 * production proofs through 2026-10-21. */
export const COMPOSED_PROOF_PRODUCTION_SLOWDOWN = 6;
/** The slowest proof seed of any catalog template, in local thread CPU:
 * mine-platformer/bounty-bob's seed 2 (a 7451-tick solver match + a
 * 6774-tick idle match), 39.2-41.0 s on 2026-10-29 with the rollout fork
 * rewound in place. (Before that it took 52 s, and its seed alone outgrew
 * the former 180 s production bound at the slowdown above.) */
export const COMPOSED_PROOF_SLOWEST_SEED_LOCAL_CPU_MS = 41_000;
/** The thread CPU one admission proof seed — its solver match and its idle
 * match, the work of that seed's solver-proof test file in the build's
 * tests gate — may take: the slowest catalog seed at the production
 * slowdown, rounded up to a whole minute. A longer match is legitimate
 * while it fits the proof's tick bounds; this bounds only what one costs.
 * The build's command-gate timeout is derived from it (measuredBuildWindow). */
export const COMPOSED_PROOF_SEED_MAX_CPU_MS = Math.ceil(
  COMPOSED_PROOF_SLOWEST_SEED_LOCAL_CPU_MS * COMPOSED_PROOF_PRODUCTION_SLOWDOWN / 60_000) * 60_000;
/** Wall reserve of a proof beyond its slowest seed: the stub checks queued
 * behind the seeds on the Builder's four proof workers. */
export const COMPOSED_PROOF_STUB_RESERVE_MS = 2 * 60_000;
/** Wall bound of one admission proof on the Builder's worker threads. */
export const COMPOSED_PROOF_TIMEOUT_MS = COMPOSED_PROOF_SEED_MAX_CPU_MS + COMPOSED_PROOF_STUB_RESERVE_MS;
/** A conversion's two-seed 900-tick visible-feedback execution probe. A
 * representative lander probe is measured locally and sized at the existing
 * 6x production slowdown with reserve; timeout is inconclusive, not absence. */
export const COMPOSED_CONVERSION_FEEDBACK_PROBE_TIMEOUT_MS = 60_000;
/** One monotonic wall bound shared by all reviewed scripted checks per candidate. */
export const COMPOSED_REVIEWED_RULE_PROBE_TIMEOUT_MS = 60_000;
/** The Builder's worst-case synthesis: every round's call and proof at their bounds, then the free check. */
export const COMPOSED_SYNTHESIS_BUDGET_MS = COMPOSED_SPEC_MAX_CALLS * (COMPOSED_SPEC_CALL_TIMEOUT_MS + COMPOSED_PROOF_TIMEOUT_MS)
  + (COMPOSED_SPEC_MAX_CALLS + 1) * COMPOSED_CONVERSION_FEEDBACK_PROBE_TIMEOUT_MS
  + COMPOSED_SPEC_MAX_CALLS * COMPOSED_REVIEWED_RULE_PROBE_TIMEOUT_MS
  // A fresh Create's last check: the game with no tuning, one more proof.
  + COMPOSED_PROOF_TIMEOUT_MS + COMPOSED_REVIEWED_RULE_PROBE_TIMEOUT_MS;

/** Each outer hop outlives the hop it waits on by this much (transport,
 * re-derivation and receipt signing, error delivery). */
const HOP_MARGIN_MS = 30_000;
/** The Function's request to the Builder's `/concept/composed-spec`. */
export const COMPOSED_SPEC_REQUEST_TIMEOUT_MS = COMPOSED_SYNTHESIS_BUDGET_MS + HOP_MARGIN_MS;
/** `synthesizeComposedSpecFn`'s own timeout (Functions gen2 allows up to 3600 s). */
export const COMPOSED_SPEC_FUNCTION_TIMEOUT_SECONDS = (COMPOSED_SPEC_REQUEST_TIMEOUT_MS + COMPOSED_REVIEWED_RULE_PROBE_TIMEOUT_MS + HOP_MARGIN_MS) / 1000;
/** The portal's callable wait for `synthesizeComposedSpecFn`. */
export const COMPOSED_SPEC_CLIENT_TIMEOUT_MS = COMPOSED_SPEC_FUNCTION_TIMEOUT_SECONDS * 1000 + HOP_MARGIN_MS;

/** Generating ONE planned rule (module generation's own budget): at most
 * three Opus calls, each writing a module + its integration (~10k tokens,
 * thinking included), every reply checked deterministically. */
export const COMPOSED_RULE_MAX_CALLS = 3;
/** Wall bound of one module-generation call. Production `world.balloonField`
 * exhausted the former six-minute bound before returning any tokens; the
 * generated-rule eval lane already uses this ten-minute ceiling. */
export const COMPOSED_RULE_CALL_TIMEOUT_MS = 10 * 60_000;
/** The Function's request to the Builder's `/concept/composed-rule`. */
export const COMPOSED_RULE_REQUEST_TIMEOUT_MS =
  COMPOSED_RULE_MAX_CALLS * (COMPOSED_RULE_CALL_TIMEOUT_MS + COMPOSED_PROOF_TIMEOUT_MS + COMPOSED_REVIEWED_RULE_PROBE_TIMEOUT_MS) + HOP_MARGIN_MS;
/** `generateComposedRuleFn`'s own timeout. */
export const COMPOSED_RULE_FUNCTION_TIMEOUT_SECONDS = (COMPOSED_RULE_REQUEST_TIMEOUT_MS + HOP_MARGIN_MS) / 1000;
/** The portal's callable wait for `generateComposedRuleFn`. */
export const COMPOSED_RULE_CLIENT_TIMEOUT_MS = COMPOSED_RULE_FUNCTION_TIMEOUT_SECONDS * 1000 + HOP_MARGIN_MS;

/** ADAPTING a template's structure (the plan's adapt level): at most three
 * Opus calls, each followed by the admission proof of the adapted game. */
export const COMPOSED_ADAPTATION_MAX_CALLS = 3;
/** Wall bound of one adaptation call. */
export const COMPOSED_ADAPTATION_CALL_TIMEOUT_MS = 6 * 60_000;
/** The Function's request to the Builder's `/concept/composed-adaptation`. */
export const COMPOSED_ADAPTATION_REQUEST_TIMEOUT_MS =
  COMPOSED_ADAPTATION_MAX_CALLS * (COMPOSED_ADAPTATION_CALL_TIMEOUT_MS + COMPOSED_PROOF_TIMEOUT_MS) + HOP_MARGIN_MS;
/** `adaptComposedTemplateFn`'s own timeout. */
export const COMPOSED_ADAPTATION_FUNCTION_TIMEOUT_SECONDS = (COMPOSED_ADAPTATION_REQUEST_TIMEOUT_MS + HOP_MARGIN_MS) / 1000;
/** The portal's callable wait for `adaptComposedTemplateFn`. */
export const COMPOSED_ADAPTATION_CLIENT_TIMEOUT_MS = COMPOSED_ADAPTATION_FUNCTION_TIMEOUT_SECONDS * 1000 + HOP_MARGIN_MS;

/** COMPOSING a whole game as a generated template (the plan's generate
 * level): at most three Opus calls, each followed by the admission proof of
 * the composed game. */
export const COMPOSED_TEMPLATE_MAX_CALLS = 3;
/** Wall bound of one template-composition call. */
export const COMPOSED_TEMPLATE_CALL_TIMEOUT_MS = 6 * 60_000;
/** The Function's request to the Builder's `/concept/composed-template`. */
export const COMPOSED_TEMPLATE_REQUEST_TIMEOUT_MS =
  COMPOSED_TEMPLATE_MAX_CALLS * (COMPOSED_TEMPLATE_CALL_TIMEOUT_MS + COMPOSED_PROOF_TIMEOUT_MS) + HOP_MARGIN_MS;
/** `generateComposedTemplateFn`'s own timeout. */
export const COMPOSED_TEMPLATE_FUNCTION_TIMEOUT_SECONDS = (COMPOSED_TEMPLATE_REQUEST_TIMEOUT_MS + HOP_MARGIN_MS) / 1000;
/** The portal's callable wait for `generateComposedTemplateFn`. */
export const COMPOSED_TEMPLATE_CLIENT_TIMEOUT_MS = COMPOSED_TEMPLATE_FUNCTION_TIMEOUT_SECONDS * 1000 + HOP_MARGIN_MS;

/** The compose PLAN (a Create's first step): one bounded review-lane call of
 * up to 16k output tokens (adaptive thinking headroom), one repair, and only
 * after verified convergence one final text or bounded structural repair.
 * Each logical call shares its wall signal with chatBounded's truncation retry. */
export const COMPOSE_PLAN_MAX_CALLS = 3;
/** Wall bound of one plan call (its truncation retry included). */
export const COMPOSE_PLAN_CALL_TIMEOUT_MS = 5 * 60_000;
/** The Function's request to the Builder's `/concept/compose-plan`. */
export const COMPOSE_PLAN_REQUEST_TIMEOUT_MS = COMPOSE_PLAN_MAX_CALLS * COMPOSE_PLAN_CALL_TIMEOUT_MS + HOP_MARGIN_MS;
/** `planComposedGameFn`'s own timeout. */
export const COMPOSE_PLAN_FUNCTION_TIMEOUT_SECONDS = (COMPOSE_PLAN_REQUEST_TIMEOUT_MS + HOP_MARGIN_MS) / 1000;
/** The portal's callable wait for `planComposedGameFn`. */
export const COMPOSE_PLAN_CLIENT_TIMEOUT_MS = COMPOSE_PLAN_FUNCTION_TIMEOUT_SECONDS * 1000 + HOP_MARGIN_MS;
